import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export interface RuntimeConfig {
  identityProvider?: string;
  conciergeOnly?: boolean;
  cognitoDomain: string;
  cognitoClientId: string;
  cognitoRegion: string;
  apiUrl: string;
  portalUrl: string;
  /** Optional — Google Maps Platform key for Places autocomplete on the New Home form. */
  googleMapsApiKey?: string;
}

const ConfigContext = createContext<RuntimeConfig | null>(null);

// Every route depends on the runtime config; a hung fetch would leave the whole
// portal wedged at "Loading…" with no recourse but a manual reload. Cap the
// request and let the operator retry inline instead.
const CONFIG_FETCH_TIMEOUT_MS = 10_000;

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const load = useCallback(() => {
    setError(null);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT_MS);

    fetch('/config.json', { signal: controller.signal, cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error(`config.json HTTP ${r.status}`);
        return r.json();
      })
      .then((cfg) => {
        if (!cancelled) setConfig(cfg);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.name === 'AbortError') {
          setError(`config.json request timed out after ${CONFIG_FETCH_TIMEOUT_MS / 1000}s`);
        } else {
          setError(e?.message || String(e));
        }
      })
      .finally(() => clearTimeout(timer));

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [attempt]);

  if (error) {
    return (
      <div className="p-8 text-alert-breach">
        <h1 className="text-xl font-bold">Configuration error</h1>
        <p className="mt-2">{error}</p>
        <p className="mt-2 text-sm text-slate-600">
          The portal couldn't load /config.json. Make sure the CDK stack uploaded it.
        </p>
        <button
          type="button"
          onClick={load}
          className="mt-4 rounded border border-alert-breach px-3 py-1 text-sm font-medium hover:bg-alert-breach hover:text-white"
        >
          Retry
        </button>
      </div>
    );
  }
  if (!config) {
    return <div className="p-8 text-slate-600">Loading…</div>;
  }
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig(): RuntimeConfig {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used inside ConfigProvider');
  return ctx;
}
